import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TCampaignGroup } from '@feathr/blackbox';
import type { ButtonType } from '@feathr/components';
import { Button, Tooltip } from '@feathr/components';
import { useToggle } from '@feathr/hooks';

import type { IAddCampaignModalProps } from './AddCampaignModal';
import AddCampaignModal from './AddCampaignModal';

interface IProps extends Omit<IAddCampaignModalProps, 'onClose'> {
  campaignGroup?: TCampaignGroup;
  children?: ReactNode;
  className?: string;
  prefix?: ReactNode;
  tooltip?: ReactNode;
  type?: ButtonType;
}

function getButtonLabel(t: TFunction, campaignGroup: TCampaignGroup): string {
  const buttonLabelMap: Record<TCampaignGroup, string> = {
    ads: t('Create ad campaign'),
    all: t('Create'),
    email: t('Create email'),
    monetization: t('Create monetization campaign'),
    'google-ads': t('New campaign'),
    other: t('Create campaign'),
  };
  return buttonLabelMap[campaignGroup];
}

function AddCampaignButton({
  campaignGroup,
  children,
  className,
  prefix,
  tooltip,
  type = 'primary',
  ...modalProps
}: IProps): JSX.Element {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const { t } = useTranslation();

  function handleClose(): void {
    toggleIsModalOpen();
  }

  const triggerProps = {
    className,
    name: 'add_campaign',
    onClick: toggleIsModalOpen,
    prefix: prefix ?? prefix !== null ? <FontAwesomeIcon icon={faPlus} /> : undefined,
  };

  const trigger = (
    <Button {...triggerProps} type={type}>
      {children ?? (campaignGroup && getButtonLabel(t, campaignGroup))}
    </Button>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{trigger}</Tooltip> : trigger}
      {isModalOpen && (
        <AddCampaignModal {...modalProps} campaignGroup={campaignGroup} onClose={handleClose} />
      )}
    </>
  );
}

export default observer(AddCampaignButton);
